import React, { useRef, forwardRef, useCallback } from "react";
import clsx from "clsx";

type GanttBarProps = {
  left: number;
  width: number;
  color: string;
  title: string;
  onDrag?: (deltaDays: number) => void;
  onResizeStart?: (deltaDays: number) => void;
  onResizeEnd?: (deltaDays: number) => void;
  progress?: number;
  progressColor?: string;
  showProgressBar?: boolean;
  onStartDependency?: () => void;
  onCompleteDependency?: () => void;
  style?: React.CSSProperties;
  className?: string; // <-- ADICIONE ESTA LINHA
};

export const GanttBar = forwardRef<HTMLDivElement, GanttBarProps>(
  (
    {
      left,
      width,
      color,
      title,
      onDrag,
      onResizeStart,
      onResizeEnd,
      progress = 0,
      progressColor = "#22c55e",
      showProgressBar = false,
      onStartDependency,
      onCompleteDependency,
      style,
      className,
    },
    ref
  ) => {
    const barRef = useRef<HTMLDivElement>(null);

    // Memoized handler para drag da barra inteira
    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (!onDrag) return;
        const startX = e.clientX;
        document.body.style.userSelect = "none";
        const handleMove = (ev: MouseEvent) => {
          const delta = Math.round((ev.clientX - startX) / 28); // 28px = 1 dia
          onDrag(delta);
        };
        const handleUp = () => {
          document.removeEventListener("mousemove", handleMove);
          document.removeEventListener("mouseup", handleUp);
          document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleUp);
      },
      [onDrag]
    );

    // Memoized handler para resize
    const handleResize = useCallback(
      (side: "start" | "end") => (e: React.MouseEvent) => {
        if (!(onResizeStart && onResizeEnd)) return;
        const startX = e.clientX;
        document.body.style.userSelect = "none";
        const handleMove = (ev: MouseEvent) => {
          const delta = Math.round((ev.clientX - startX) / 28);
          if (side === "start") onResizeStart(delta);
          else onResizeEnd(delta);
        };
        const handleUp = () => {
          document.removeEventListener("mousemove", handleMove);
          document.removeEventListener("mouseup", handleUp);
          document.body.style.userSelect = "";
        };
        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleUp);
      },
      [onResizeStart, onResizeEnd]
    );

    // Memoized handler para dependências
    const handleCompleteDependency = useCallback(
      (e?: React.MouseEvent | React.KeyboardEvent) => {
        if (e) e.stopPropagation?.();
        if (onCompleteDependency) onCompleteDependency();
      },
      [onCompleteDependency]
    );

    const handleStartDependency = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onStartDependency) onStartDependency();
      },
      [onStartDependency]
    );

    return (
      <div
        ref={ref ?? barRef}
        className={clsx(
          "absolute flex items-center font-medium overflow-hidden whitespace-nowrap cursor-pointer px-2 rounded-xl transition-all",
          "h-7 min-w-[20px] shadow-lg border border-white/30",
          "bg-gradient-to-r from-blue-400/80 to-blue-600/80",
          "backdrop-blur-md",
          "font-grotesk", // <-- Use grotesk para a barra
          "hover:scale-[1.03] hover:shadow-xl active:scale-[0.98]",
          className
        )}
        style={{
          left: `${left}%`,
          width: `${width}%`,
          background: color,
          zIndex: 2,
          ...style,
        }}
        title={title}
        role="button"
        aria-label={title}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter") handleCompleteDependency(e);
        }}
        onMouseDown={onDrag ? handleMouseDown : undefined}
        onClick={handleCompleteDependency}
      >
        {/* Handle left */}
        <div
          className="h-full w-2 cursor-ew-resize bg-white opacity-40 rounded-l absolute left-0 top-0 z-10"
          onMouseDown={e => { e.stopPropagation(); handleResize("start")(e); }}
          title="Redimensionar início"
        />
        {/* Barra de progresso interna */}
        {showProgressBar && (
          <div
            className="absolute left-0 top-0 h-full z-0"
            style={{
              width: `${Math.max(0, Math.min(progress, 100))}%`,
              background: progressColor,
              opacity: 0.4,
              borderRadius: "inherit",
            }}
            aria-label={`Progresso: ${progress}%`}
          />
        )}
        <span className="mx-auto w-full truncate relative z-10 font-inter">{title}</span>
        {/* Handle right */}
        <div
          className="h-full w-2 cursor-ew-resize bg-white opacity-40 rounded-r absolute right-0 top-0 z-10"
          onMouseDown={e => { e.stopPropagation(); handleResize("end")(e); }}
          title="Redimensionar fim"
        />
        {/* Handle para criar dependência (lado direito) */}
        {onStartDependency && (
          <div
            className="absolute right-[-8px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-blue-400 border-2 border-white cursor-crosshair z-20"
            title="Criar dependência"
            onClick={handleStartDependency}
            style={{ boxShadow: "0 0 2px #0003" }}
          />
        )}
      </div>
    );
  }
);
GanttBar.displayName = "GanttBar";